import { SerpHotelPositionsSortType } from '@/entities/analytics';
import {
  BeachLine,
  BeachType,
  ChildService,
  DistanceLift,
  HotelFacility,
  RenovationType,
  RestType,
  WaterActivity,
} from '@/entities/hotel';
import { HotelCategoryId } from '@/entities/hotelCategory';
import { MealId } from '@/entities/meal';
import type { ProductSearchErrorPhase } from '@/entities/product';
import { ProductType } from '@/shared/config/pages';
import { FilterGroupId, type SerpSort } from '../model/types';

export const SERP_TRACKING_PRODUCT_TYPES: Partial<Record<ProductType, string>> = {
  [ProductType.TourOffer]: 'TourProduct',
};

export const SERP_TRACKING_SORT_TYPES: Readonly<Record<SerpSort, SerpHotelPositionsSortType>> = {
  popular: SerpHotelPositionsSortType.Recommend,
  priceAsc: SerpHotelPositionsSortType.PriceUp,
  priceDesc: SerpHotelPositionsSortType.PriceDown,
  rating: SerpHotelPositionsSortType.RatingDown,
};

export const SERP_TRACKING_FAILED_PHASES: ReadonlySet<ProductSearchErrorPhase> = new Set(['session', 'final', 'run']);

export const SERP_TRACKING_COUNTRIES_COUNT = 3;

export const SERP_TRACKING_RESORTS_COUNT = 5;

export const SERP_TRACKING_HOTELS_COUNT = 5;

export const SERP_TRACKING_HOTEL_CATEGORY_FIELDS: Readonly<Record<string, HotelCategoryId>> = {
  hotelCategory_5: HotelCategoryId.FiveStars,
  hotelCategory_4: HotelCategoryId.FourStars,
  hotelCategory_3: HotelCategoryId.ThreeStars,
  hotelCategory_2: HotelCategoryId.TwoStars,
  // как trackingTourCriteria старого фронта: у туров hotelCategory_1 смотрит на категорию 2
  hotelCategory_1: HotelCategoryId.TwoStars,
  hotelCategory_0: HotelCategoryId.NoStars,
  hotelCategory_restBase: HotelCategoryId.RecreationCenter,
  hotelCategory_pension: HotelCategoryId.BoardingHouse,
  hotelCategory_sanatorium: HotelCategoryId.Sanatorium,
  hotelCategory_apartments: HotelCategoryId.Apartments,
  hotelCategory_hostel: HotelCategoryId.Hostel,
};

export const SERP_TRACKING_MEAL_FIELDS: Readonly<Record<string, MealId>> = {
  meal_uai: MealId.UltraAllInclusive,
  meal_ai: MealId.AllInclusive,
  meal_ai_no_alc: MealId.AllInclusiveNoAlcohol,
  meal_bb: MealId.Breakfast,
  meal_lhb: MealId.BreakfastAndLunch,
  meal_fb: MealId.FullBoard,
  meal_hb: MealId.HalfBoard,
  meal_ro: MealId.RoomOnly,
};

export interface SerpTrackingFilterOption {
  groupId: FilterGroupId;
  key: string;
}

export const SERP_TRACKING_FILTER_FIELDS: Readonly<Record<string, SerpTrackingFilterOption | null>> = {
  sandBeach: { groupId: FilterGroupId.BeachType, key: BeachType.SandBeach },
  pebbleBeach: { groupId: FilterGroupId.BeachType, key: BeachType.PebbleBeach },
  sandPebbleBeach: { groupId: FilterGroupId.BeachType, key: BeachType.SandPebbleBeach },
  firstLine: { groupId: FilterGroupId.BeachLine, key: BeachLine.First },
  secondLine: { groupId: FilterGroupId.BeachLine, key: BeachLine.Second },
  thirdLine: { groupId: FilterGroupId.BeachLine, key: BeachLine.Third },
  beachOwner: { groupId: FilterGroupId.BeachOwner, key: FilterGroupId.BeachOwner },
  nearTheCenter: { groupId: FilterGroupId.NearTheCenter, key: FilterGroupId.NearTheCenter },
  exclusive: { groupId: FilterGroupId.IsExclusive, key: FilterGroupId.IsExclusive },
  best: { groupId: FilterGroupId.Rating, key: 'best' },
  better: { groupId: FilterGroupId.Rating, key: 'better' },
  good: { groupId: FilterGroupId.Rating, key: 'good' },
  restBeach: { groupId: FilterGroupId.RestType, key: RestType.Beach },
  restCity: { groupId: FilterGroupId.RestType, key: RestType.City },
  mountains: { groupId: FilterGroupId.RestType, key: RestType.Mountain },
  wellness: { groupId: FilterGroupId.RestType, key: RestType.Wellness },
  eco: { groupId: FilterGroupId.RestType, key: RestType.Eco },
  rightEntrance: { groupId: FilterGroupId.DistanceLift, key: DistanceLift.SkiIn },
  walkingDistance: { groupId: FilterGroupId.DistanceLift, key: DistanceLift.Walking },
  busDistance: { groupId: FilterGroupId.DistanceLift, key: DistanceLift.Bus },
  openPool: { groupId: FilterGroupId.WaterActivities, key: WaterActivity.OpenPool },
  closePool: { groupId: FilterGroupId.WaterActivities, key: WaterActivity.IndoorPool },
  heatedPool: { groupId: FilterGroupId.WaterActivities, key: WaterActivity.HeatedPool },
  seaPool: { groupId: FilterGroupId.WaterActivities, key: WaterActivity.SeaWaterPool },
  waterslides: { groupId: FilterGroupId.WaterActivities, key: WaterActivity.WaterSlides },
  aquapark: { groupId: FilterGroupId.WaterActivities, key: WaterActivity.Aquapark },
  lonelyMan: { groupId: FilterGroupId.Facilities, key: HotelFacility.LonelyMan },
  airConditioner: { groupId: FilterGroupId.Facilities, key: HotelFacility.AirConditioner },
  wifi: { groupId: FilterGroupId.Facilities, key: HotelFacility.Wifi },
  spaAndHealth: { groupId: FilterGroupId.Facilities, key: HotelFacility.SpaAndHealth },
  bayLocated: { groupId: FilterGroupId.Facilities, key: HotelFacility.BayLocated },
  adultsOnly: { groupId: FilterGroupId.Facilities, key: HotelFacility.AdultsOnly },
  withKids: { groupId: FilterGroupId.Facilities, key: HotelFacility.WithKids },
  forCouples: { groupId: FilterGroupId.Facilities, key: HotelFacility.ForCouples },
  withAnimals: { groupId: FilterGroupId.Facilities, key: HotelFacility.WithAnimals },
  parking: { groupId: FilterGroupId.Facilities, key: HotelFacility.Parking },
  saunaBathHammam: { groupId: FilterGroupId.Facilities, key: HotelFacility.SaunaBathHammam },
  jacuzzi: { groupId: FilterGroupId.Facilities, key: HotelFacility.Jacuzzi },
  children_animators: { groupId: FilterGroupId.ChildServices, key: ChildService.Animators },
  kidsPool: { groupId: FilterGroupId.ChildServices, key: ChildService.Pool },
  children_childmenu: { groupId: FilterGroupId.ChildServices, key: ChildService.Menu },
  children_childBed: { groupId: FilterGroupId.ChildServices, key: ChildService.Bed },
  children_nanny: { groupId: FilterGroupId.ChildServices, key: ChildService.Nanny },
  children_clubs: { groupId: FilterGroupId.ChildServices, key: ChildService.Clubs },
  children_playroom: { groupId: FilterGroupId.ChildServices, key: ChildService.Playroom },
  recently_opened: { groupId: FilterGroupId.Renovation, key: RenovationType.RecentlyOpened },
  fresh_renovation: { groupId: FilterGroupId.Renovation, key: RenovationType.FreshRenovation },
  nearTheLift: null,
  cashback: null,
  nearTheBeach: null,
  pool: null,
  mirCashback: null,
};
